package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

// ---------- Hero ----------

val FormsHeroFlowStyle = CssStyle.base {
    Modifier.css("margin-top" to "10px")
}

// ---------- Demo: tabs, form and inbox preview ----------

val FormsTabsStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "margin-bottom" to "24px")
}

val FormsTabStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.Paper).color(RdColor.Ink).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(999.px)
            .cursor(Cursor.Pointer).css("font" to "500 .92rem/1 ${RdFont.BODY_STACK}", "padding" to "11px 16px")
    }
    hover { Modifier.css("border-color" to "rgb(46,75,110)") }
}

val FormsTabActiveStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Brand).color(RdColor.BrandInk).css("border-color" to "rgb(46,75,110)")
}

val FormsDemoGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "clamp(20px, 3vw, 40px)", "align-items" to "start") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "1fr 1fr") }
}

val FormsCardStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).gap(14.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(18.px).css("padding" to "clamp(20px, 3vw, 28px)", "min-width" to "0", "box-shadow" to RdSize.SHADOW)
    }
}

val FormsCardTitleStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).justifyContent(JustifyContent.SpaceBetween).alignItems(AlignItems.Center).gap(10.px).css("flex-wrap" to "wrap") }
    cssRule(" b") { Modifier.fontFamily(*RdFont.DISPLAY).css("font-size" to "1.15rem", "font-weight" to 800, "font-stretch" to "108%") }
    cssRule(" span") { Modifier.color(RdColor.Muted).css("font" to "500 .68rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase") }
}

/** Look shared by every input, text area and dropdown in the demo forms. */
private val FormsControlModifier = Modifier.color(RdColor.Ink).backgroundColor(RdColor.Ground).border(1.px, LineStyle.Solid, RdColor.Line)
    .borderRadius(10.px).css("font" to "400 1rem/1.4 ${RdFont.BODY_STACK}", "padding" to "12px 14px", "width" to "100%")

/** A labelled text field, text area, dropdown or date picker. */
val FormsFieldStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(6.px).css("font-size" to ".88rem", "font-weight" to 600) }
    cssRule(" input") { Modifier.then(FormsControlModifier) }
    cssRule(" textarea") { Modifier.then(FormsControlModifier).css("min-height" to "110px", "resize" to "vertical") }
    cssRule(" select") { Modifier.then(FormsControlModifier) }
    cssRule(" input:focus") { Modifier.css("outline" to "2px solid rgb(46,75,110)", "border-color" to "transparent") }
    cssRule(" textarea:focus") { Modifier.css("outline" to "2px solid rgb(46,75,110)", "border-color" to "transparent") }
    cssRule(" select:focus") { Modifier.css("outline" to "2px solid rgb(46,75,110)", "border-color" to "transparent") }
}

val FormsRequiredStyle = CssStyle.base { Modifier.color(RdColor.Brand) }

/** A group of radio buttons or checkboxes shown as pills. */
val FormsChoicesStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(8.px).css("border" to "0", "margin" to "0", "padding" to "0", "min-width" to "0") }
    cssRule(" legend") { Modifier.css("font-size" to ".88rem", "font-weight" to 600, "padding" to "0", "margin-bottom" to "8px") }
    cssRule(" > div") { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap") }
    cssRule(" label") {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(7.px).backgroundColor(RdColor.Ground)
            .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(999.px).cursor(Cursor.Pointer)
            .css("font-size" to ".88rem", "font-weight" to 500, "padding" to "7px 12px")
    }
    cssRule(" input") { Modifier.css("accent-color" to "rgb(46,75,110)", "margin" to "0") }
}

val FormsChoiceCheckedStyle = CssStyle.base {
    Modifier.css("border-color" to "rgb(46,75,110) !important", "background" to "rgb(230,237,245) !important")
}

val FormsSubmitCardStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(14.px).css("margin-top" to "14px", "padding" to "16px") }
    cssRule(" > button") { Modifier.css("justify-self" to "start") }
}

val FormsSpamNoteStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(10.px).color(RdColor.Muted).backgroundColor(RdColor.Ground)
        .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(10.px).css("font-size" to ".84rem", "padding" to "10px 12px")
}

val FormsSentStyle = CssStyle.base {
    Modifier.color(RdColor.Brand).css("font-size" to ".9rem", "font-weight" to 600)
}

val FormsInboxStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(18.px)
            .css("overflow" to "hidden", "min-width" to "0", "box-shadow" to RdSize.SHADOW)
    }
    Breakpoint.LG { Modifier.position(Position.Sticky).css("top" to "calc(env(safe-area-inset-top, 0px) + 92px)") }
}

val FormsInboxBarStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(10.px).backgroundColor(RdColor.Band)
            .color(RdColor.BandText).css("padding" to "12px 16px", "font-size" to ".85rem")
    }
    cssRule(" svg") { Modifier.color(RdColor.LogoBlue) }
    cssRule(" span:last-child") {
        Modifier.color(RdColor.BandMuted).css("margin-left" to "auto", "font" to "500 .68rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
    }
}

val FormsMailStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(12.px).css("padding" to "18px 20px 22px")
}

val FormsMailHeadStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(4.px).css("padding-bottom" to "12px", "border-bottom" to "1px solid rgb(220,227,236)") }
    cssRule(" b") { Modifier.css("font-size" to "1.05rem") }
    cssRule(" span") { Modifier.color(RdColor.Muted).css("font-size" to ".82rem") }
}

val FormsMailBodyStyle = CssStyle {
    // Labels capped at 40% so a long one ("Preferred appointment date") wraps instead of squeezing the values.
    base { grid("minmax(0, 40%) minmax(0, 1fr)").css("gap" to "10px 16px", "margin" to "0", "font-size" to ".92rem") }
    cssRule(" dt") { Modifier.color(RdColor.Muted) }
    cssRule(" dd") { Modifier.css("margin" to "0", "overflow-wrap" to "anywhere", "white-space" to "pre-line") }
}

val FormsMailEmptyStyle = CssStyle.base { Modifier.color(RdColor.Line) }

val FormsInboxFootStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).backgroundColor(RdColor.Ground)
        .css("font-size" to ".84rem", "padding" to "12px 20px", "border-top" to "1px solid rgb(220,227,236)")
}

// ---------- Field types ----------

val FormsFieldTypesStyle = CssStyle {
    // One column on small phones, two from SM; minmax(0, 1fr) lets cards shrink below their sample fields.
    base { grid("minmax(0, 1fr)").gap(16.px) }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "minmax(0, 1fr) minmax(0, 1fr)") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "repeat(4, minmax(0, 1fr))") }
    cssRule(" > div") {
        Modifier.display(DisplayStyle.Grid).gap(12.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(14.px).css("align-content" to "start", "padding" to "18px", "min-width" to "0")
    }
    cssRule(" b") { Modifier.css("font-size" to ".98rem") }
}

/** A small drawing of a form control, not a working one. */
val FormsMiniControlStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(8.px).color(RdColor.Muted).backgroundColor(RdColor.Ground)
        .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(9.px).css("min-height" to "38px", "padding" to "8px 12px", "font-size" to ".85rem")
}

val FormsMiniAreaStyle = CssStyle.base { Modifier.alignItems(AlignItems.FlexStart).css("min-height" to "62px") }
val FormsMiniSelectStyle = CssStyle.base { Modifier.justifyContent(JustifyContent.SpaceBetween) }

val FormsMiniBoxStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Brand).borderRadius(4.px).css("width" to "15px", "height" to "15px", "border" to "2px solid rgb(46,75,110)")
}

val FormsMiniDotStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).borderRadius(50.px).css("width" to "15px", "height" to "15px", "place-items" to "center", "border" to "2px solid rgb(46,75,110)") }
    cssRule("::after") { Modifier.backgroundColor(RdColor.Brand).css("content" to "\"\"", "width" to "7px", "height" to "7px", "border-radius" to "50%") }
}

// ---------- Spam band ----------

val FormsSpamStyle = CssStyle {
    base { Modifier.backgroundColor(RdColor.Band).color(RdColor.BandText) }
    cssRule(" .rd-eyebrow") { Modifier.color(RdColor.BandMuted) }
}

val FormsSpamGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "28px clamp(28px, 4vw, 56px)", "align-items" to "start") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "auto 1fr 1fr") }
}

val FormsShieldStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).color(RdColor.LogoBlue).borderRadius(24.px)
        .css("width" to "88px", "height" to "88px", "place-items" to "center", "background" to "rgba(180,198,220,.14)", "border" to "1px solid rgba(180,198,220,.4)")
}

val FormsSpamCopyStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(14.px) }
    cssRule(" p") { Modifier.color(RdColor.BandMuted) }
}

val FormsSpamListStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(12.px).css("align-content" to "start") }
    cssRule(" > p") { Modifier.color(RdColor.LogoBlue).css("font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase") }
    cssRule(" ul") { Modifier.display(DisplayStyle.Grid).gap(10.px).css("list-style" to "none", "padding" to "0") }
    cssRule(" li") { Modifier.display(DisplayStyle.Grid).gap(10.px).css("grid-template-columns" to "22px 1fr") }
    cssRule(" li::before") { Modifier.color(RdColor.LogoBlue).css("content" to "\"✓\"", "font-weight" to 700) }
}
